/* eslint-disable import/order */
import React, { useEffect } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';

import SpotlightCard from 'components/SpotlightCard/SpotlightCard';
import { ObjectItem } from 'api/clients/types';

import useCustomMarkerFuncs from '../hooks/useCustomMarkerFuncs';

import './CustomMarker.scss';
import ObjectData from './ObjectData';
import PopupButtonsArea from './PopupButtonsArea';
import ActivityCard from './ActivityCard';

interface CustomMarkerProps {
  object_data: ObjectItem;
  isDraggable: boolean;
  onDragEnd?: (objData: ObjectItem, newPos: [number, number]) => void;
  onToggleDraggable?: (objData: ObjectItem) => void;
  onSavePositionSuccess?: (objData: ObjectItem) => void;
  markerRef?: (ref: any) => void;
  isActivity?: string;
}

const CustomMarker: React.FC<CustomMarkerProps> = ({
  object_data,
  isActivity,
  isDraggable,
  onDragEnd,
  onToggleDraggable,
  onSavePositionSuccess,
  markerRef: externalMarkerRef,
}) => {
  const map = useMap();
  const { t } = useTranslation('translation', { keyPrefix: 'ClientDashboardV2' });
  const { markerRef, markerPosition, isLoading, handleSavePosition, handleToggleDraggable, eventHandlers } =
    useCustomMarkerFuncs({
      object_data,
      isDraggable,
      onDragEnd,
      onToggleDraggable,
      onSavePositionSuccess,
    });

  // Expose internal ref to parent through callback
  useEffect(() => {
    if (externalMarkerRef && markerRef.current) {
      externalMarkerRef(markerRef.current);
    }
  }, [externalMarkerRef, markerRef]);

  const getColorByStatus = (status: string) => {
    switch (status) {
      case 'danger':
        return 'rgba(240, 101, 72, 0.2)';
      case 'warning':
        return 'rgba(255, 193, 7, 0.2)';
      case 'success':
        return 'rgba(40, 167, 69, 0.2)';
      default:
        return 'rgba(130, 19, 221, 0.2)';
    }
  };
  const getColorByStatusBorder = (status: string) => {
    switch (status) {
      case 'danger':
        return 'border-5 border-danger';
      case 'warning':
        return 'border-5 border-warning';
      case 'success':
        return 'border-0';
      default:
        return 'border-0';
    }
  };
  const getMarkerBorderStyle = (status: string) => {
    switch (status) {
      case 'danger':
        // Червоний колір
        return 'border-left: 4px solid rgb(240, 101, 72); border-right: 4px solid rgb(240, 101, 72);';
      case 'warning':
        // Жовтий колір
        return 'border-left: 4px solid rgb(255, 193, 7); border-right: 4px solid rgb(255, 193, 7);';
      case 'success':
      default:
        // Без рамок
        return '';
    }
  };
  const customIcon = L.divIcon({
    className: '',
    html: `
        <div 
          style="
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 120px !important;
            height: 70px;
            position: relative;
          "
        >
          <!-- The white box (with relative positioning for icon overlay) -->
          <div 
            style="
              background: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              min-width: 120px !important;
              padding: 8px;
              border-radius: 6px;
              text-align: center;
              position: relative;
             ${getMarkerBorderStyle(object_data.activity_status)}
            "
          >
            <!-- Title text -->
            <div 
              style="
                color: black;
                font-weight: bold;
                font-size: 14px;
                white-space: nowrap;
              "
            >
              ${object_data.name || t('unnamed')}
            </div>
          </div>
    
          <!-- The arrow -->
          <div
            style="
              width: 0;
              height: 0;
              border-left: 8px solid transparent;
              border-right: 8px solid transparent;
              border-top: 8px solid white;
              margin-top: -2px;
            "
          ></div>
        </div>
      `,
    iconSize: [120, 70],
    popupAnchor: [0, -20],
    iconAnchor: [60, 10],
  });

  const popupWidth = window.innerWidth > 344 ? 450 : 300;
  const popupHeight =
    window.innerWidth <= 430
      ? window.innerHeight - 350
      : window.innerHeight < 900
        ? window.innerHeight - 340
        : window.innerHeight;

  return (
    <>
      <Marker
        position={markerPosition}
        icon={customIcon}
        draggable={isDraggable}
        eventHandlers={{
          ...eventHandlers,
          click: e => {
            L.DomEvent.stopPropagation(e.originalEvent);

            const handlers = eventHandlers as any;
            if (handlers && handlers.click) {
              handlers.click(e);
            }

            setTimeout(() => {
              const popupElement = document.querySelector('.marker-popup-content') as HTMLElement;

              if (popupElement) {
                const actualHeight = popupElement.offsetHeight;

                const px = map.project(e.latlng, map.getZoom());

                if (window.innerWidth < 950) {
                  px.y -= actualHeight * 0.16;
                } else {
                  px.y -= actualHeight * 0.5;
                }
                map.panTo(map.unproject(px, map.getZoom()), { animate: true });
              }
            }, 50);
          },
        }}
        ref={markerRef}
        // @ts-ignore
        isActivity={isActivity}
      >
        <Popup
          autoPan={false}
          maxWidth={popupWidth}
          minWidth={popupWidth}
          className="invisible-popup marker-popup-content"
          closeButton={false}
          maxHeight={popupHeight}
        >
          <span className="cursor-pointer">
            <SpotlightCard
              className={`custom-spotlight-card grey-card ${getColorByStatusBorder(object_data.activity_status)}`}
              spotlightColor={getColorByStatus(object_data.activity_status)}
            >
              <div className="popup_scroller" style={{ maxHeight: popupHeight - 74 }}>
                <ObjectData object_data={object_data} />

                <ActivityCard object_data={object_data} />

                <PopupButtonsArea
                  isDraggable={isDraggable}
                  isLoading={isLoading}
                  handleSavePosition={handleSavePosition}
                  handleToggleDraggable={handleToggleDraggable}
                  objectSlug={object_data.slug}
                />
              </div>
            </SpotlightCard>
          </span>
        </Popup>
      </Marker>
    </>
  );
};

export default CustomMarker;
